import _ from 'underscore';
import { useMemo, useContext } from 'react';
import useFetch from 'use-http';

import { CampaignFetchContext } from './use-campaign-fetch-provider';

const makeAbsoluteUrl = (url) => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return new URL(url, window.location.href).href;
};


class FetchError extends Error {
  constructor(errors) {
    super('An CMAPI fetch error occurred');
    this.error = errors || [];
  }
}

export const useCampaignFetch = (...args) => {
  const context = useContext(CampaignFetchContext);
  const url = args[0] || makeAbsoluteUrl(context.url);
  const options = args[1] || context.options;
  const {
    request,
    response,
    loading,
    data,
    error,
  } = useFetch(url, options, args[2] || undefined);

  const wrappedRequest = useMemo(() => {
    const del = getCallProxy(withCacheInvalidation(request.del, request.cache));
    return {
      ...request,
      get: getCallProxy(request.get),
      post: getCallProxy(withCacheInvalidation(request.post, request.cache)),
      put: getCallProxy(withCacheInvalidation(request.put, request.cache)),
      patch: getCallProxy(withCacheInvalidation(request.patch, request.cache)),
      del,
      delete: del,
    };
  }, [request]);

  // for details on the expected response shape, see https://github.com/ava/use-http/blob/master/src/useFetch.ts
  return Object.assign(
    [wrappedRequest, response, loading, error],
    {
      request: wrappedRequest,
      response,
      ...wrappedRequest,
      loading,
      data,
      error,
    }
  );
};

function withCacheInvalidation(call, cache) {
  return (...args) => call(...args).then((response) => {
    cache.clear();
    return response;
  });
}

function getCallProxy(call) {
  return (...args) => call(...args).then(getData);
}

function getData(response) {
  if (response == null) {
    return null;
  } else if (_.isArray(response.OperationErrors) && !_.isEmpty(response.OperationErrors)) {
    const errors = [];
    _.each(response.OperationErrors, (err) => {
      if (!_.isEmpty(err && err.Message)) {
        errors.push({
          Code: err.Code,
          Message: err.Message,
          Details: err.Details,
          ErrorCode: err.ErrorCode,
        });
      }
    });
    throw new FetchError(errors);
  } else {
    return response;
  }
}
